'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { getDailyPanchang, DailyPanchangRecord } from '@/lib/api/client';
import { ExportButton } from '@/components/ui/ExportButton';
import { Sun, Moon, Clock, MapPin, Calendar, AlertTriangle, ShieldCheck, Sparkles, Compass } from 'lucide-react';
import { formatLocalizedTime } from '@/lib/i18n/dateFormat';

const POPULAR_CITIES = [
  { name: 'New Delhi', lat: 28.6139, lon: 77.2090 },
  { name: 'Mumbai', lat: 19.0760, lon: 72.8777 },
  { name: 'Bengaluru', lat: 12.9716, lon: 77.5946 },
  { name: 'Kolkata', lat: 22.5726, lon: 88.3639 },
  { name: 'Chennai', lat: 13.0827, lon: 80.2707 },
  { name: 'Varanasi', lat: 25.3176, lon: 82.9739 },
  { name: 'London, UK', lat: 51.5074, lon: -0.1278 },
  { name: 'New York, US', lat: 40.7128, lon: -74.0060 }
];

export interface PanchangClientProps {
  initialData?: DailyPanchangRecord | null;
  initialError?: string | null;
}

export function PanchangClient({
  initialData = null,
  initialError = null
}: PanchangClientProps) {
  const t = useTranslations('panchangDaily');
  const tGlossary = useTranslations('glossary');
  const locale = useLocale();

  const resultRef = useRef<HTMLDivElement>(null);
  const [selectedCity, setSelectedCity] = useState(POPULAR_CITIES[0]);
  const [date, setDate] = useState<string>('');

  const [data, setData] = useState<DailyPanchangRecord | null>(initialData);
  const [loading, setLoading] = useState(initialData ? false : true);
  const [error, setError] = useState<string | null>(initialError);

  const fetchPanchang = (lat: number, lon: number, dStr?: string) => {
    setLoading(true);
    setError(null);
    getDailyPanchang({ lat, lon, date: dStr || undefined })
      .then((res) => setData(res))
      .catch((err) => setError(err.message || t('calculationFailed')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!initialData) {
      fetchPanchang(selectedCity.lat, selectedCity.lon, date);
    }
  }, [selectedCity, date]);

  const handleCityChange = (cityName: string) => {
    const city = POPULAR_CITIES.find((c) => c.name === cityName);
    if (city) {
      setSelectedCity(city);
    }
  };

  const formatDisplayTime = (timeStr: string) => {
    if (!timeStr) return '';
    return formatLocalizedTime(timeStr, locale);
  };

  const getCityName = (cityName: string) => {
    try {
      return t(`cities.${cityName}` as any) || cityName;
    } catch {
      return cityName;
    }
  };

  const getVaraName = (record: DailyPanchangRecord) => {
    const dayNum = String(record.panchang.vara.day_number);
    return tGlossary(`weekdays.${dayNum}` as any) || record.panchang.vara.english_name;
  };

  const getVaraSanskrit = (record: DailyPanchangRecord) => {
    const dayNum = String(record.panchang.vara.day_number);
    return tGlossary(`weekdaysSanskrit.${dayNum}` as any) || record.panchang.vara.sanskrit_name;
  };

  const getVaraRuler = (record: DailyPanchangRecord) => {
    const r = record.panchang.vara.ruler;
    return tGlossary(`planets.${r}` as any) || r;
  };

  const getTithiName = (record: DailyPanchangRecord) => {
    const num = String(record.panchang.tithi.tithi_number);
    const baseName = tGlossary(`tithis.${num}` as any) || record.panchang.tithi.name;
    const pakshaName = tGlossary(`paksha.${record.panchang.tithi.paksha}` as any) || record.panchang.tithi.paksha;
    return `${pakshaName} ${baseName}`;
  };

  const getNakshatraName = (record: DailyPanchangRecord) => {
    const id = String(record.panchang.nakshatra.nakshatra_id + 1);
    return tGlossary(`nakshatras.${id}` as any) || record.panchang.nakshatra.name;
  };

  const getNakshatraLord = (record: DailyPanchangRecord) => {
    const l = record.panchang.nakshatra.lord;
    return tGlossary(`planets.${l}` as any) || l;
  };

  const getYogaName = (record: DailyPanchangRecord) => {
    const num = String(record.panchang.yoga.yoga_number);
    return tGlossary(`yogas.${num}` as any) || record.panchang.yoga.name;
  };

  const getKaranaName = (record: DailyPanchangRecord) => {
    const num = String(record.panchang.karana.karana_number);
    return tGlossary(`karanas.${num}` as any) || record.panchang.karana.name;
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto" data-testid="daily-panchang-client">
      {/* Header Banner */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-[#5ab4ff] text-[10px] font-bold uppercase tracking-wider mb-2">
              <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
              <span>{t('eyebrow')}</span>
            </div>
            <h1 className="text-3xl font-['Lexend'] font-medium text-white mb-2">
              {t('title')}
            </h1>
            <p className="text-[#dcecff]/75 text-xs sm:text-sm max-w-2xl font-['Outfit']">
              {t('subtitle')}
            </p>
          </div>

          {/* Location & Date Selectors */}
          <div className="flex flex-wrap items-center gap-3 bg-white/[0.03] p-3 rounded-2xl border border-white/10">
            <div className="flex items-center gap-1.5 text-xs text-[#dcecff]/70">
              <MapPin className="w-3.5 h-3.5 text-[#5ab4ff]" />
              <select
                value={selectedCity.name}
                onChange={(e) => handleCityChange(e.target.value)}
                className="bg-[#050a24] border border-white/12 text-white text-xs rounded-xl px-3 py-2 focus:ring-1 focus:ring-[#5ab4ff] focus:outline-none cursor-pointer"
              >
                {POPULAR_CITIES.map((c) => (
                  <option key={c.name} value={c.name} className="bg-[#050a24] text-white">
                    {getCityName(c.name)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-[#dcecff]/70">
              <Calendar className="w-3.5 h-3.5 text-[#5ab4ff]" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-[#050a24] border border-white/12 text-white text-xs rounded-xl px-3 py-2 focus:ring-1 focus:ring-[#5ab4ff] focus:outline-none cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-12 text-center text-[#dcecff]/60 text-xs animate-pulse font-['Outfit']">
          {t('calculatingCity', { cityName: getCityName(selectedCity.name) })}
        </div>
      ) : error ? (
        <div data-testid="daily-panchang-error" className="bg-[#ff9bb0]/10 border border-[#ff9bb0]/30 rounded-3xl p-6 text-[#ff9bb0] text-xs">
          {error}
        </div>
      ) : data ? (
        <div className="space-y-6" data-testid="daily-panchang-result">
          {/* Export Action Bar */}
          <div className="no-print flex items-center justify-between gap-4 bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-2xl p-4 px-6 shadow-md backdrop-blur-xl">
            <span className="text-xs font-['Lexend'] font-semibold text-white">
              {t('resultsHeading')}
            </span>
            <ExportButton
              targetRef={resultRef}
              filename={`astropredict-panchang-${data.date || new Date().toISOString().split('T')[0]}`}
              label={t('exportLabel')}
            />
          </div>

          {/* Captured Result Section */}
          <div ref={resultRef} className="space-y-8 p-6 sm:p-8 bg-[rgba(5,10,36,0.92)] rounded-3xl border border-white/16 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl">
            {/* Sunrise & Sunset Widget Header */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                <div className="p-3 bg-[#ffd36b]/10 border border-[#ffd36b]/20 rounded-xl text-[#ffd36b]">
                  <Sun className="w-6 h-6 text-[#ffd36b]" />
                </div>
                <div>
                  <span className="text-xs text-[#dcecff]/60 block font-['Outfit']">{t('sunrise')}</span>
                  <span className="text-lg font-bold font-mono text-white">{formatDisplayTime(data.sun_times.sunrise)}</span>
                </div>
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                <div className="p-3 bg-[#b9c6ff]/10 border border-[#b9c6ff]/20 rounded-xl text-[#b9c6ff]">
                  <Moon className="w-6 h-6 text-[#b9c6ff]" />
                </div>
                <div>
                  <span className="text-xs text-[#dcecff]/60 block font-['Outfit']">{t('sunset')}</span>
                  <span className="text-lg font-bold font-mono text-white">{formatDisplayTime(data.sun_times.sunset)}</span>
                </div>
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                <div className="p-3 bg-[#5ab4ff]/10 border border-[#5ab4ff]/20 rounded-xl text-[#5ab4ff]">
                  <Clock className="w-6 h-6 text-[#5ab4ff]" />
                </div>
                <div>
                  <span className="text-xs text-[#dcecff]/60 block font-['Outfit']">{t('varaHeading')}</span>
                  <span className="text-xs font-bold text-white font-['Lexend']">
                    {getVaraName(data)} ({getVaraSanskrit(data)})
                  </span>
                  <span className="text-xs text-[#5ab4ff] block font-['Outfit']">
                    {t('rulerLabel', { ruler: getVaraRuler(data) })}
                  </span>
                </div>
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-center gap-4">
                <div className="p-3 bg-[#8dffc9]/10 border border-[#8dffc9]/20 rounded-xl text-[#8dffc9]">
                  <MapPin className="w-6 h-6 text-[#8dffc9]" />
                </div>
                <div>
                  <span className="text-xs text-[#dcecff]/60 block font-['Outfit']">{t('locationLabel')}</span>
                  <span className="text-xs font-bold text-white font-['Lexend']">{getCityName(selectedCity.name)}</span>
                  <span className="text-xs text-[#dcecff]/60 block font-mono">
                    {data.location.latitude.toFixed(2)} deg, {data.location.longitude.toFixed(2)} deg
                  </span>
                </div>
              </div>
            </div>

            {/* Prominent Muhurtas Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-['Lexend'] font-medium text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#5ab4ff]" />
                <span>{t('muhurtaHeading')}</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* PROMINENT RAHU KAAL CARD */}
                <div className="bg-white/[0.03] border border-[#ff9bb0]/40 rounded-2xl p-5 shadow-sm relative overflow-hidden">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-[#ff9bb0] font-semibold text-xs font-['Lexend']">
                      <AlertTriangle className="w-4 h-4 text-[#ff9bb0]" />
                      <span>{tGlossary('muhurtaNames.rahu_kaal')}</span>
                    </div>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-[#ff9bb0]/15 text-[#ff9bb0] border border-[#ff9bb0]/30">
                      {tGlossary('muhurtaTypes.inauspicious')}
                    </span>
                  </div>
                  <p className="text-xs text-[#dcecff]/70 mb-3 font-['Outfit']">{t('rahuKaalDesc')}</p>
                  <div className="text-lg font-bold font-mono text-[#ff9bb0] bg-black/30 p-2.5 rounded-xl border border-white/10 text-center">
                    {formatDisplayTime(data.muhurtas.rahu_kaal.display)}
                  </div>
                </div>

                {/* Yamaganda */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white text-xs font-['Lexend']">{tGlossary('muhurtaNames.yamaganda')}</span>
                    <span className="text-[10px] font-medium px-2.5 py-0.5 rounded-full bg-white/[0.05] text-[#dcecff]/70 border border-white/10">
                      {tGlossary('muhurtaTypes.inauspicious')}
                    </span>
                  </div>
                  <p className="text-xs text-[#dcecff]/70 mb-3 font-['Outfit']">{t('yamagandaDesc')}</p>
                  <div className="text-base font-bold font-mono text-white bg-black/30 p-2.5 rounded-xl border border-white/10 text-center">
                    {formatDisplayTime(data.muhurtas.yamaganda.display)}
                  </div>
                </div>

                {/* Gulika Kaal */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white text-xs font-['Lexend']">{tGlossary('muhurtaNames.gulika_kaal')}</span>
                    <span className="text-[10px] font-medium px-2.5 py-0.5 rounded-full bg-white/[0.05] text-[#dcecff]/70 border border-white/10">
                      {tGlossary('muhurtaTypes.inauspicious')}
                    </span>
                  </div>
                  <p className="text-xs text-[#dcecff]/70 mb-3 font-['Outfit']">{t('gulikaKaalDesc')}</p>
                  <div className="text-base font-bold font-mono text-white bg-black/30 p-2.5 rounded-xl border border-white/10 text-center">
                    {formatDisplayTime(data.muhurtas.gulika_kaal.display)}
                  </div>
                </div>

                {/* Abhijit Muhurat (Auspicious) */}
                <div className="bg-white/[0.03] border border-[#8dffc9]/30 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[#8dffc9] font-semibold text-xs font-['Lexend']">
                      <ShieldCheck className="w-4 h-4 text-[#8dffc9]" />
                      <span>{tGlossary('muhurtaNames.abhijit_muhurat')}</span>
                    </div>
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-[#8dffc9]/15 text-[#8dffc9] border border-[#8dffc9]/30">
                      {tGlossary('muhurtaTypes.auspicious')}
                    </span>
                  </div>
                  <p className="text-xs text-[#dcecff]/70 mb-3 font-['Outfit']">{t('abhijitDesc')}</p>
                  <div className="text-base font-bold font-mono text-[#8dffc9] bg-black/30 p-2.5 rounded-xl border border-white/10 text-center">
                    {formatDisplayTime(data.muhurtas.abhijit_muhurat.display)}
                  </div>
                </div>
              </div>
            </div>

            {/* 5 Core Panchang Elements Grid */}
            <div className="space-y-4">
              <h2 className="text-xl font-['Lexend'] font-medium text-white flex items-center gap-2">
                <Compass className="w-5 h-5 text-[#5ab4ff]" />
                <span>{t('core5Heading')}</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {/* Tithi */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 space-y-3">
                  <span className="text-[10px] text-[#5ab4ff] uppercase font-bold tracking-wider block font-mono">{t('tithiHeading')}</span>
                  <div>
                    <h3 className="text-base font-['Lexend'] font-semibold text-white">{getTithiName(data)}</h3>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#5ab4ff] h-full rounded-full"
                      style={{ width: `${data.panchang.tithi.progress_percent}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-[#dcecff]/60 block font-mono">
                    {t('elapsed', { percent: data.panchang.tithi.progress_percent })}
                  </span>
                </div>

                {/* Nakshatra */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 space-y-3">
                  <span className="text-[10px] text-[#b9c6ff] uppercase font-bold tracking-wider block font-mono">{t('nakshatraHeading')}</span>
                  <div>
                    <h3 className="text-base font-['Lexend'] font-semibold text-white">{getNakshatraName(data)}</h3>
                    <span className="text-xs text-[#dcecff]/70 block font-['Outfit']">
                      {t('padaLabel', { pada: data.panchang.nakshatra.pada })} · {t('lordLabel', { lord: getNakshatraLord(data) })}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#b9c6ff] h-full rounded-full"
                      style={{ width: `${data.panchang.nakshatra.progress_percent}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-[#dcecff]/60 block font-mono">
                    {t('elapsed', { percent: data.panchang.nakshatra.progress_percent })}
                  </span>
                </div>

                {/* Yoga */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 space-y-3">
                  <span className="text-[10px] text-[#ffd36b] uppercase font-bold tracking-wider block font-mono">{t('yogaHeading')}</span>
                  <div>
                    <h3 className="text-base font-['Lexend'] font-semibold text-white">{getYogaName(data)}</h3>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#ffd36b] h-full rounded-full"
                      style={{ width: `${data.panchang.yoga.progress_percent}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-[#dcecff]/60 block font-mono">
                    {t('elapsed', { percent: data.panchang.yoga.progress_percent })}
                  </span>
                </div>

                {/* Karana */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 space-y-3">
                  <span className="text-[10px] text-[#8dffc9] uppercase font-bold tracking-wider block font-mono">{t('karanaHeading')}</span>
                  <div>
                    <h3 className="text-base font-['Lexend'] font-semibold text-white">{getKaranaName(data)}</h3>
                    <span className="text-xs text-[#dcecff]/70 block font-['Outfit']">
                      {data.panchang.karana.is_fixed ? tGlossary('karanaTypes.fixed') : tGlossary('karanaTypes.movable')}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
                    <div
                      className="bg-[#8dffc9] h-full rounded-full"
                      style={{ width: `${data.panchang.karana.progress_percent}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-[#dcecff]/60 block font-mono">
                    {t('elapsed', { percent: data.panchang.karana.progress_percent })}
                  </span>
                </div>

                {/* Vara */}
                <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 space-y-3">
                  <span className="text-[10px] text-[#ff9bb0] uppercase font-bold tracking-wider block font-mono">{t('varaElementHeading')}</span>
                  <div>
                    <h3 className="text-base font-['Lexend'] font-semibold text-white">{getVaraName(data)}</h3>
                    <span className="text-xs text-[#dcecff]/70 block font-['Outfit']">
                      {getVaraSanskrit(data)}
                    </span>
                  </div>
                  <span className="text-xs text-[#ff9bb0] block font-mono">
                    {t('rulerLabel', { ruler: getVaraRuler(data) })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
